import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Plugin } from "chart.js";
import { MdVolumeOff, MdVolumeUp } from "react-icons/md";
import RichText from '../../components/common/RichText';
import "./ResultsPage.css";

ChartJS.register(ArcElement, Tooltip, Legend);

interface Result {
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  skippedQuestions: number;
  percentage: number;
  detailedResults: Array<{
    questionId: string;
    question: string;
    options: string[];
    correctAnswer: string;
    explanationToAnswer: string;
    userAnswer: string | null;
  }>;
  subject: string;
  feedback: string;
  topics: string[];
  Botfeedback: string;
  audioBase64: string;
}

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { result } = (location.state as { result: Result }) || {};
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [key, setKey] = useState(0);

  const getCssVar = useCallback((name: string, fallback: string) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }, []);

  useEffect(() => {
    if (!result) return;
    const handleBackButton = () => {
      navigate("/dashboard", { replace: true });
    };

    window.addEventListener('popstate', handleBackButton);
    // Register the centerText plugin
    const centerTextPlugin: Plugin = {
      id: 'centerText',
      afterDraw: (chart: any) => {
        const { ctx, chartArea: { left, right, top, bottom } } = chart;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Draw percentage
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = getCssVar('--text-1', '#111827');
        ctx.fillText(`${result.percentage.toFixed(2)}%`, centerX, centerY - 10);

        // Draw "Score" text
        ctx.font = '14px Arial';
        ctx.fillStyle = getCssVar('--text-3', '#64748b');
        ctx.fillText('Score', centerX, centerY + 10);

        ctx.restore();
      }
    };

    // Register new plugin
    ChartJS.register(centerTextPlugin);

    if (result?.audioBase64) {
      setAudioSrc(`data:audio/mp3;base64,${result.audioBase64}`);
    }
    setKey(prev => prev + 1);

    // Cleanup on unmount
    return () => {
      const plugin = ChartJS.registry.plugins.get('centerText');
      if (plugin) {
        ChartJS.unregister(plugin);
      }
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [navigate, result, getCssVar]);

  if (!result) {
    return (
      <div className="results-error">
        No results available. Please try again.
      </div>
    );
  }

  const total = result.totalQuestions;
  const correctPercentage = (result.correctAnswers / total) * 100;
  const incorrectPercentage = (result.incorrectAnswers / total) * 100;
  const skippedPercentage = (result.skippedQuestions / total) * 100;

  const data = {
    labels: ["Correct", "Incorrect", "Skipped"],
    datasets: [
      {
        data: [
          result.correctAnswers,
          result.incorrectAnswers,
          result.skippedQuestions,
        ],
        backgroundColor: ["#28a745", "#dc3545", "#ffc107"],
        borderColor: [getCssVar('--surface-0', '#ffffff'), getCssVar('--surface-0', '#ffffff'), getCssVar('--surface-0', '#ffffff')],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    cutout: "70%",
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: getCssVar('--text-2', '#334155'),
        },
      },
      tooltip: {
        backgroundColor: getCssVar('--surface-2', 'rgba(15, 23, 42, 0.92)'),
        titleColor: getCssVar('--text-1', '#ffffff'),
        bodyColor: getCssVar('--text-1', '#ffffff'),
        callbacks: {
          label: (context: any) => {
            const label = context.label || "";
            const value = context.parsed || 0;
            return `${label}: ${value} questions`;
          },
        },
      },
    },
  };

  const handleViewDetailedResults = () => {
    navigate("/detailed-results", {
      state: { result },
      replace: true // Replace current history entry to prevent state accumulation
    });
  };

  const handleGoHome = () => {
    navigate("/dashboard");
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(audioRef.current.muted);
    }
  };

  return (
    <>
      <div className="results-page">
        <div className="results-container" key={key}>
          <h1 className="results-title">📊 Quiz Results</h1>
          <div className="results-chart">
            <Pie data={data} options={options} key={`chart-${key}`} />
          </div>
          <div className="results-summary">
            <h2>{result.subject} Quiz</h2>

            <p>Total Questions: {result.totalQuestions}</p>
            <p>Correct Answers: {result.correctAnswers} ({correctPercentage.toFixed(2)}%)</p>
            <p>Incorrect Answers: {result.incorrectAnswers} ({incorrectPercentage.toFixed(2)}%)</p>
            <p>Skipped Questions: {result.skippedQuestions} ({skippedPercentage.toFixed(2)}%)</p>
            <p>Score: {result.percentage.toFixed(2)}%</p>
          </div>
        </div>
        <div className="feedback-section">
          <div className="feedback-header">
            <h3>Feedback:</h3>
            <button className="mute-button" onClick={toggleMute}>
              {isMuted ? <MdVolumeOff size={24} /> : <MdVolumeUp size={24} />}
            </button>
          </div>
          <div className="markdown-content">
            <RichText text={result.Botfeedback} />
          </div>
          {audioSrc && (
            <div className="audio-controls">
              <audio ref={audioRef} controls autoPlay className="results-audio">
                <source src={audioSrc} type="audio/mp3" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}
        </div>
        <div className="down-button">
          <button
            className="detailed-results-button"
            onClick={handleViewDetailedResults}
          >
            View Detailed Results
          </button>
          <button
            className="detailed-results-button"
            onClick={handleGoHome}
          >
            Go Home
          </button>
        </div>
      </div>
    </>
  );
};

export default ResultsPage;
